/**
 * An app in a frame, and the host side of the bridge that answers it.
 *
 * The panel asks omnium for the session, draws the app's iframe, and answers
 * the app's messages with admin API calls made under the panel's own sign-in.
 * No token, cookie or omnium address goes into the frame.
 *
 * Design: docs/design/workflow-platform/04-bridge.md.
 */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import {
  BridgeError,
  createHost,
  createHttpApi,
  normaliseOrigin,
  type CommandResult,
  type Host,
  type HostApi,
  type ServerSession,
  type SubjectRef,
  type WindowLike,
} from "@fanos/omnium-bridge";

import { Button, LinkButton } from "@/components/kit";
import { getToken, onSignedOut } from "@/lib/api/auth";
import { getAdminApiBaseUrl } from "@/lib/api/config";
import {
  liveState,
  pathForOpen,
  problemFor,
  READY_WAIT_MS,
  readyIn,
  reportingEventSource,
  type FrameProblem,
  type Handshake,
  type Live,
  type Stream,
} from "@/lib/bridge/frame";
import { useTheme } from "@/spa/useTheme";

import styles from "./AppFrame.module.css";

export type FrameInfo = {
  /** `null` until omnium has answered. */
  session: ServerSession | null;
  live: Live;
};

type Props = {
  /** The app's code, as registered. */
  app: string;
  /** The workflow code the app is opened for. */
  workflow: string;
  subject: SubjectRef;
  /** The event a fixture must belong to. Set it for an event's app. */
  event?: string | null;
  /** Drawn above the frame, with what is known so far. */
  bar?: (info: FrameInfo) => ReactNode;
  /** Called with every command result the app gets back. */
  onCommand?: (result: CommandResult) => void;
};

const SANDBOX = "allow-scripts allow-same-origin allow-forms allow-popups allow-downloads";

const OFF: Live = { tone: "off", label: "Not connected" };

type Loaded = { key: string; session: ServerSession | null; problem: FrameProblem | null };

function subjectKey(subject: SubjectRef): string {
  return subject.kind === "event" ? `event:${subject.code}` : `fixture:${subject.id}`;
}

export function AppFrame({ app, workflow, subject, event = null, bar, onCommand }: Props) {
  const navigate = useNavigate();
  const theme = useTheme();
  const [attempt, setAttempt] = useState(0);
  const key = JSON.stringify([app, workflow, subjectKey(subject), event, attempt]);

  // ---------------------------------------------------------------- session

  const [loaded, setLoaded] = useState<Loaded | null>(null);
  useEffect(() => {
    let alive = true;
    const [appCode, workflowCode, subjectText, eventCode] = JSON.parse(key) as [
      string,
      string,
      string,
      string | null,
    ];
    const [kind, ref] = [subjectText.slice(0, subjectText.indexOf(":")), subjectText.slice(subjectText.indexOf(":") + 1)];
    const target: SubjectRef = kind === "event" ? { kind: "event", code: ref } : { kind: "fixture", id: ref };
    createHttpApi({ baseUrl: getAdminApiBaseUrl(), token: getToken })
      .session({ app: appCode, workflow: workflowCode, subject: target, event: eventCode })
      .then(
        (session) => alive && setLoaded({ key, session, problem: null }),
        (error: unknown) => alive && setLoaded({ key, session: null, problem: problemFor(error) }),
      );
    return () => {
      alive = false;
    };
  }, [key]);

  const current = loaded?.key === key ? loaded : null;
  const session = current?.session ?? null;

  // ---------------------------------------------------------------- the host

  const frameRef = useRef<HTMLIFrameElement>(null);
  const hostRef = useRef<Host | null>(null);
  const themeRef = useRef(theme);
  const sentThemeRef = useRef(theme);
  const onCommandRef = useRef(onCommand);
  const navigateRef = useRef(navigate);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    onCommandRef.current = onCommand;
    navigateRef.current = navigate;
  });

  const [handshake, setHandshake] = useState<{ of: ServerSession | null; state: Handshake }>({
    of: null,
    state: "waiting",
  });
  const [stream, setStream] = useState<{ of: ServerSession | null; state: Stream }>({
    of: null,
    state: "connecting",
  });
  const [ended, setEnded] = useState(false);

  // A layout effect, so the host is listening before the frame can say `ready`.
  useLayoutEffect(() => {
    if (!session) return;
    const Source = reportingEventSource((state) => setStream({ of: session, state }));
    const http = createHttpApi({
      baseUrl: getAdminApiBaseUrl(),
      token: getToken,
      event,
      live:
        session.subject.kind === "event"
          ? { event: session.subject.code }
          : { fixture: session.subject.id },
      EventSource: Source,
    });
    const api: HostApi = {
      ...http,
      async command(workflowCode, target, body) {
        const result = await http.command(workflowCode, target, body);
        onCommandRef.current?.(result);
        return result;
      },
    };
    const host = createHost({
      frame: () => (frameRef.current?.contentWindow ?? null) as unknown as WindowLike | null,
      session: { ...session, hostOrigin: window.location.origin, theme: themeRef.current },
      api,
      // A workflow published after this page opened offers more than this
      // session lists. The host asks omnium for the session again before it tells
      // the app no, so nobody has to reload the page to use a new command.
      reloadSession: async () => ({
        ...(await http.session({
          app: session.app.code,
          workflow: session.workflow.code,
          subject:
            session.subject.kind === "event"
              ? { kind: "event", code: session.subject.code ?? "" }
              : { kind: "fixture", id: session.subject.id },
          event,
        })),
        hostOrigin: window.location.origin,
        theme: themeRef.current,
      }),
      onOpen: (target, ref) => {
        const path = pathForOpen(target, ref);
        if (!path) throw new BridgeError("not_offered", "omnium has no page for that yet.");
        navigateRef.current(path);
      },
    });
    hostRef.current = host;
    sentThemeRef.current = themeRef.current;

    // Watch for the app's `ready` too, only to tell the person whether it connected.
    const appOrigin = normaliseOrigin(session.app.origin);
    const listen = (message: MessageEvent) => {
      if (message.source !== frameRef.current?.contentWindow) return;
      if (normaliseOrigin(message.origin) !== appOrigin) return;
      const said = readyIn(message.data);
      if (!said) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      setHandshake({ of: session, state: said === "ready" ? "connected" : "update" });
    };
    window.addEventListener("message", listen);

    return () => {
      window.removeEventListener("message", listen);
      if (timerRef.current) clearTimeout(timerRef.current);
      host.dispose();
      if (hostRef.current === host) hostRef.current = null;
    };
  }, [session, event]);

  // Theme changes reach the app without opening it again.
  useEffect(() => {
    themeRef.current = theme;
    if (hostRef.current && sentThemeRef.current !== theme) {
      hostRef.current.setTheme(theme);
      sentThemeRef.current = theme;
    }
  }, [theme]);

  // Signing out ends the app's session.
  useEffect(
    () =>
      onSignedOut(() => {
        hostRef.current?.end("logged_out");
        hostRef.current = null;
        setEnded(true);
      }),
    [],
  );

  const onLoad = () => {
    if (!session) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setHandshake((now) =>
        now.of === session && now.state !== "waiting" ? now : { of: session, state: "silent" },
      );
    }, READY_WAIT_MS);
  };

  // ---------------------------------------------------------------- drawing

  const handshakeNow = handshake.of === session && session ? handshake.state : "waiting";
  const canStream = typeof EventSource !== "undefined";
  const streamNow: Stream = !canStream
    ? "polling"
    : stream.of === session && session
      ? stream.state
      : "connecting";
  const live = ended || current?.problem ? OFF : liveState(handshakeNow, streamNow);

  let body: ReactNode;
  if (!current) {
    body = <FrameState title="Opening…" />;
  } else if (current.problem) {
    const { problem } = current;
    body = (
      <FrameState title={problem.title} note={problem.note}>
        {problem.retry && (
          <Button tone="primary" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </Button>
        )}
        {problem.kind === "not_set_up" && <LinkButton to="/workflows">Open workflows</LinkButton>}
        {problem.kind === "sign_in" && (
          <LinkButton to="/login" tone="primary">
            Sign in
          </LinkButton>
        )}
      </FrameState>
    );
  } else if (session) {
    body = (
      <>
        {/* The frame stays, so the app can show its own "session ended" screen. */}
        {ended && (
          <div className={styles.notice} data-tone="bad" role="alert">
            <strong>You signed out.</strong>
            <span>The app has stopped. Sign in again to go on.</span>
            <LinkButton to="/login">Sign in</LinkButton>
          </div>
        )}
        {!ended && (handshakeNow === "update" || handshakeNow === "silent") && (
          <div className={styles.notice} role="status">
            <strong>This app needs an update.</strong>
            <span>
              {handshakeNow === "update"
                ? "It uses an older version of the omnium bridge, so it cannot read or save anything."
                : "It did not answer omnium. It may still use the old way of talking to the panel, so it cannot read or save anything."}
            </span>
          </div>
        )}
        <iframe
          ref={frameRef}
          className={styles.frame}
          src={session.app.url}
          title={session.app.name}
          sandbox={SANDBOX}
          allow="clipboard-write"
          onLoad={onLoad}
        />
      </>
    );
  }

  return (
    <div className={styles.shell}>
      {bar?.({ session, live })}
      {body}
    </div>
  );
}

/** What a frame shows instead of the app: opening, a problem, or the end of a session. */
export function FrameState({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children?: ReactNode;
}) {
  return (
    <div className={styles.state} role={note ? "alert" : "status"}>
      <strong className={styles.stateTitle}>{title}</strong>
      {note && <span className={styles.stateNote}>{note}</span>}
      {children && <div className={styles.stateActions}>{children}</div>}
    </div>
  );
}

/** The live dot: a word and a dot, never colour alone. */
export function LiveDot({ live }: { live: Live }) {
  return (
    <span className={styles.live} data-tone={live.tone}>
      <span className={styles.liveDot} aria-hidden />
      {live.label}
    </span>
  );
}
